export type NumericFilterOperator =
  | 'between'
  | 'lessThan'
  | 'greaterThan'
  | 'equals'
  | 'notEqual'
  | 'isEmpty'
  | 'isNotEmpty';

export interface NumericFilterCondition {
  operator: NumericFilterOperator;
  value1: number | null;
  value2: number | null;
}

export const DEFAULT_NUMERIC_CONDITION: NumericFilterCondition = {
  operator: 'between',
  value1: null,
  value2: null,
};

export interface FilterDisplayOption {
  hideZero: boolean;
  hideBlanks: boolean;
}

export const DEFAULT_FILTER_DISPLAY_OPTION: FilterDisplayOption = {
  hideZero: false,
  hideBlanks: false,
};

/** Filter IDs that support per-filter Hide 0 / Hide blanks. */
export const NUMERIC_FILTER_IDS = [
  'accountId',
  'counterparty',
  'currency',
  'caStatus',
  'caType',
  'primaryAccountName',
  'primarySettlementCp',
  'exDate',
  'quantity',
  'settledQuantity',
  'quantityT1',
  'quantityT2',
  'quantityTx',
  'leverageRate',
  'valueEUR',
  'benchmarkFee',
  'activeUtilisation',
  'quantityOnLoan',
  'rebateRate',
  'currencyBenchmark',
  'dailyPotential',
  'adtv30D',
  'adtvMultiple',
  'adtvAdjusted',
  'l2cpCounterparties',
  'l2cpQuantity',
  'l2cpAmountEur',
  'l2cpRate',
  'l2cpGrossRate',
  'l2cpSnpDelta',
] as const;

export type NumericFilterId = (typeof NUMERIC_FILTER_IDS)[number];

export type FilterDisplayOptions = Partial<Record<string, FilterDisplayOption>>;

export interface LocalInventoryFilters {
  accountIds: string[];
  counterparties: string[];
  currencies: string[];
  exchanges: string[];
  caStatuses: string[];
  caTypes: string[];
  brokerAccountNames: string[];
  brokerSettlementCounterparties: string[];
  quantityCondition: NumericFilterCondition;
  settledQuantityCondition: NumericFilterCondition;
  quantityT1Condition: NumericFilterCondition;
  quantityT2Condition: NumericFilterCondition;
  quantityTxCondition: NumericFilterCondition;
  leverageRateCondition: NumericFilterCondition;
  valueEURCondition: NumericFilterCondition;
  currencyBenchmarkCondition: NumericFilterCondition;
  dailyPotentialCondition: NumericFilterCondition;
  adtv30DCondition: NumericFilterCondition;
  adtvMultipleCondition: NumericFilterCondition;
  adtvAdjustedCondition: NumericFilterCondition;
  benchmarkFeeCondition: NumericFilterCondition;
  activeUtilisationCondition: NumericFilterCondition;
  quantityOnLoanCondition: NumericFilterCondition;
  rebateRateCondition: NumericFilterCondition;
  l2cpCounterparties: string[];
  l2cpQuantityCondition: NumericFilterCondition;
  l2cpAmountEurCondition: NumericFilterCondition;
  l2cpRateCondition: NumericFilterCondition;
  l2cpGrossRateCondition: NumericFilterCondition;
  l2cpSnpDeltaCondition: NumericFilterCondition;
  pendingTransfersOutCondition: NumericFilterCondition;
  cimbEq2QuantityCondition: NumericFilterCondition;
  cimbSfQuantityCondition: NumericFilterCondition;
  cimbSf3QuantityCondition: NumericFilterCondition;
  velocityLQuantityCondition: NumericFilterCondition;
  velocityMQuantityCondition: NumericFilterCondition;
  exDateFrom: string | null;
  exDateTo: string | null;
}

export interface FilterItem {
  id: string;
  label: string;
  type:
    | 'statement'
    | 'accountId'
    | 'counterparty'
    | 'currency'
    | 'exchange'
    | 'caStatus'
    | 'caType'
    | 'brokerAccountName'
    | 'brokerSettlementCounterparty'
    | 'quantity'
    | 'settledQuantity'
    | 'quantityT1'
    | 'quantityT2'
    | 'quantityTx'
    | 'leverageRate'
    | 'valueEUR'
    | 'benchmarkFee'
    | 'activeUtilisation'
    | 'quantityOnLoan'
    | 'rebateRate'
    | 'currencyBenchmark'
    | 'dailyPotential'
    | 'adtv30D'
    | 'adtvMultiple'
    | 'adtvAdjusted'
    | 'l2cpGroup'
    | 'pendingTransfersOut'
    | 'cimbEq2'
    | 'cimbSf'
    | 'cimbSf3'
    | 'velocityL'
    | 'velocityM'
    | 'exDate'
    | 'export';
  enabled: boolean;
  order: number;
}

export interface FilterManagementState {
  filters: FilterItem[];
  isEditMode: boolean;
  hasChanges: boolean;
}

/** Filter order matches table column order (left-to-right). */
export const DEFAULT_FILTER_ITEMS: FilterItem[] = [
  { id: 'statement', label: 'Statement', type: 'statement', enabled: true, order: 0 },
  { id: 'accountId', label: 'Account ID', type: 'accountId', enabled: true, order: 1 },
  { id: 'counterparty', label: 'Counterparty', type: 'counterparty', enabled: true, order: 2 },
  { id: 'exchange', label: 'Exchange', type: 'exchange', enabled: true, order: 3 },
  { id: 'currency', label: 'Currency', type: 'currency', enabled: true, order: 4 },
  { id: 'quantity', label: 'Quantity (Trade Date)', type: 'quantity', enabled: true, order: 5 },
  { id: 'settledQuantity', label: 'Settled Quantity', type: 'settledQuantity', enabled: true, order: 6 },
  { id: 'quantityT1', label: 'Expected Settled Quantity T+1', type: 'quantityT1', enabled: true, order: 7 },
  { id: 'quantityT2', label: 'Expected Settled Quantity T+2', type: 'quantityT2', enabled: true, order: 8 },
  { id: 'quantityTx', label: 'Expected Settled Quantity T+X', type: 'quantityTx', enabled: true, order: 9 },
  { id: 'l2cpGroup', label: 'Lent to Counterparties', type: 'l2cpGroup', enabled: true, order: 10 },
  { id: 'pendingTransfersOut', label: 'Pending Transfers (out)', type: 'pendingTransfersOut', enabled: true, order: 11 },
  { id: 'leverageRate', label: 'Leverage Rate', type: 'leverageRate', enabled: true, order: 12 },
  { id: 'valueEUR', label: 'Value, EUR', type: 'valueEUR', enabled: true, order: 13 },
  { id: 'brokerSettlementCounterparty', label: 'Primary Settlement CP', type: 'brokerSettlementCounterparty', enabled: true, order: 14 },
  { id: 'brokerAccountName', label: 'Primary Account Name', type: 'brokerAccountName', enabled: true, order: 15 },
  { id: 'cimbEq2', label: 'Quantity CIMB (EQ2)', type: 'cimbEq2', enabled: true, order: 16 },
  { id: 'cimbSf', label: 'Quantity CIMB (SF)', type: 'cimbSf', enabled: true, order: 17 },
  { id: 'cimbSf3', label: 'Quantity CIMB (SF3)', type: 'cimbSf3', enabled: true, order: 18 },
  { id: 'velocityL', label: 'Quantity Velocity (L)', type: 'velocityL', enabled: true, order: 19 },
  { id: 'velocityM', label: 'Quantity Velocity (M)', type: 'velocityM', enabled: true, order: 20 },
  { id: 'exDate', label: 'Ex-Date', type: 'exDate', enabled: true, order: 21 },
  { id: 'caType', label: 'CA Type', type: 'caType', enabled: true, order: 22 },
  { id: 'caStatus', label: 'CA Status', type: 'caStatus', enabled: true, order: 23 },
  { id: 'benchmarkFee', label: 'Benchmark Fee (%)', type: 'benchmarkFee', enabled: true, order: 24 },
  { id: 'quantityOnLoan', label: 'Quantity on Loan', type: 'quantityOnLoan', enabled: true, order: 25 },
  { id: 'activeUtilisation', label: 'Active Utilisation (%)', type: 'activeUtilisation', enabled: true, order: 26 },
  { id: 'currencyBenchmark', label: 'Currency Benchmark', type: 'currencyBenchmark', enabled: true, order: 27 },
  { id: 'rebateRate', label: 'Rebate Rate (%)', type: 'rebateRate', enabled: true, order: 28 },
  { id: 'dailyPotential', label: 'Daily Potential, EUR', type: 'dailyPotential', enabled: true, order: 29 },
  { id: 'adtv30D', label: '30D ADTV', type: 'adtv30D', enabled: true, order: 30 },
  { id: 'adtvMultiple', label: 'ADTV Multiple', type: 'adtvMultiple', enabled: true, order: 31 },
  { id: 'adtvAdjusted', label: 'ADTV-Adjusted, EUR', type: 'adtvAdjusted', enabled: true, order: 32 },
  { id: 'export', label: 'Export', type: 'export', enabled: true, order: 33 },
];

